import React, { useState, useRef } from 'react';
import { Modal, Button, ProgressBar, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import * as dropboxService from '../../../../services/dropbox';

interface UploadFileModalProps {
  folderId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({ folderId, onClose, onSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allTags = useSelector((state: any) => state.dropbox.allTags || []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setSelectedFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // console.log('Starting upload with tags:', tags);

    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        // Always send folder_id (null for root folder)
        formData.append('folder_id', folderId || '');
        // All files are global by default
        formData.append('is_global', 'true');

        try {
          const response = await dropboxService.uploadFile(formData, (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          });

          // console.log(`Upload response for ${file.name}:`, response);
          // console.log(`Response type:`, typeof response);
          // console.log(`Response keys:`, response ? Object.keys(response) : 'no response');

          // Handle undefined response
          if (!response) {
            failCount++;
            console.error(`No response received for ${file.name}`);
            toast.error(`Failed to upload ${file.name}: No response from server`);
            continue;
          }

          // Success detection based on actual API response structure
          // console.log(`Checking response structure:`, {
          //   hasSuccess: 'success' in response,
          //   successValue: response.success,
          //   hasData: 'data' in response,
          //   hasFile: response.data?.file,
          //   fileId: response.data?.file?.id
          // });

          // Based on the actual API response: {"success": true, "data": {"file": {"id": "..."}}}
          let isSuccess = false;
          let fileData = null;
          let fileId = null;

          if (response.success === true && response.data?.file?.id) {
            isSuccess = true;
            fileData = response.data.file;
            fileId = response.data.file.id;
            // console.log(`✅ SUCCESS: API returned success=true with file ID: ${fileId}`);
          } else {
            // console.log(`❌ FAILED: success=${response.success}, hasFile=${!!response.data?.file}, fileId=${response.data?.file?.id}`);
          }

          // console.log(`Final decision: isSuccess=${isSuccess}, fileId=${fileId}`);

          if (isSuccess && fileId) {
            successCount++;
            // console.log(`✅ SUCCESS: ${file.name} - successCount now: ${successCount}`);
            // console.log(`Successfully uploaded ${file.name} with ID: ${fileId}`);
            
            // Add tags if any were specified
            if (tags.length > 0) {
              try {
                const tagResponse = await dropboxService.addTags(fileId, tags);
                // console.log(`Tag response for ${file.name}:`, tagResponse);
                
                if (tagResponse?.success) {
                  // console.log(`Tags added successfully to ${file.name}:`, tags);
                } else {
                  // console.warn(`Failed to add tags to ${file.name}:`, tagResponse?.message);
                  toast.warning(`File uploaded but failed to add tags to ${file.name}`);
                }
              } catch (tagError: any) {
                // console.error(`Error adding tags to ${file.name}:`, tagError);
                toast.warning(`File uploaded but failed to add tags to ${file.name}`);
              }
            }
          } else {
            failCount++;
            // console.log(`❌ FAILURE: ${file.name} - failCount now: ${failCount}`);
            const errorMsg = response?.message || response?.error || 'Upload failed';
            // console.error(`Upload failed for ${file.name}:`, response);
            // console.error(`Error details:`, {
            //   isSuccess,
            //   fileId,
            //   fileData,
            //   errorMsg,
            //   response
            // });
            toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
          }
        } catch (error: any) {
          failCount++;
          // console.log(`💥 EXCEPTION: ${file.name} - failCount now: ${failCount}`);
          const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
          toast.error(`Failed to upload ${file.name}: ${errorMsg}`);
        }
      }

      if (successCount > 0) {
        const tagMessage = tags.length > 0 ? ` with ${tags.length} tag(s)` : '';
        // console.log(`Final success toast: successCount=${successCount}, failCount=${failCount}`);
        toast.success(`Successfully uploaded ${successCount} file(s)${tagMessage}`);
        onSuccess();
      }
      
      // if (failCount > 0) {
      //   console.log(`Final status: ${successCount} succeeded, ${failCount} failed`);
      // }
      
      if (failCount === 0) {
        onClose();
      }
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleAddTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();
    // console.log('Adding tag:', trimmedTag);
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      setTagInput('');
      // console.log('Updated tags:', newTags);
    }
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (tagInput.trim()) {
        handleAddTag(tagInput);
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const suggestedTags = allTags.filter((tag: string) => 
    !tags.includes(tag) && tag.toLowerCase().includes(tagInput.toLowerCase())
  ).slice(0, 5);

  return (
    <Modal show={true} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload Files</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className={`upload-dropzone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleBrowseClick}
          style={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            padding: '3rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragActive ? '#f0f8ff' : '#fafafa',
            transition: 'all 0.3s ease',
          }}
        >
          <i className="fas fa-cloud-upload-alt fa-3x mb-3" style={{ color: '#007bff' }}></i>
          <p className="mb-2">
            <strong>Drag and drop files here</strong>
          </p>
          <p className="text-muted">or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-3">
            <h6>Selected Files ({selectedFiles.length}):</h6>
            <ul className="list-group">
              {selectedFiles.map((file, index) => (
                <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                  <div style={{ flex: 1, minWidth: 0, marginRight: '0.5rem' }}>
                    <i className="fas fa-file mr-2"></i>
                    <span 
                      style={{ 
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                      }}
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <small className="text-muted ml-2">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </small>
                  </div>
                  {!uploading && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploading && (
          <div className="mt-3">
            <p>Uploading... {uploadProgress}%</p>
            <ProgressBar now={uploadProgress} animated />
          </div>
        )}

        {!uploading && selectedFiles.length > 0 && (
          <div className="mt-4">
            <Form.Group>
              <Form.Label className="fw-semibold mb-3">
                <i className="fas fa-tags me-2"></i>
                Tags (Optional)
              </Form.Label>
              
              {/* Current Tags */}
              {tags.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
                    {tags.map((tag, index) => (
                      <span 
                        key={index} 
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          padding: '0.4rem 0.6rem',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#495057',
                          gap: '0.25rem'
                        }}
                      >
                        <i className="fas fa-tag" style={{ fontSize: '0.75rem', color: '#6c757d' }}></i>
                        <span>{tag}</span>
                        <i 
                          className="fas fa-times" 
                          onClick={() => removeTag(tag)}
                          style={{ 
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            padding: '0.25rem',
                            marginLeft: '0.25rem',
                            borderRadius: '50%',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc3545';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#6c757d';
                          }}
                        ></i>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tag Input */}
              <div className="d-flex gap-2 mb-3">
                <Form.Control
                  type="text"
                  placeholder="Type to add a tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  style={{ 
                    borderRadius: '6px',
                    padding: '0.6rem 0.75rem'
                  }}
                />
                <Button 
                  variant="outline-primary" 
                  onClick={() => handleAddTag(tagInput)}
                  disabled={!tagInput.trim()}
                  style={{ 
                    minWidth: '44px',
                    borderRadius: '6px'
                  }}
                >
                  <i className="fas fa-plus"></i>
                </Button>
              </div>

              {/* Suggested Tags */}
              {tagInput && suggestedTags.length > 0 && (
                <div>
                  <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                    Suggestions:
                  </small>
                  <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
                    {suggestedTags.map((tag: string, index: number) => (
                      <span 
                        key={index}
                        className="d-inline-flex align-items-center"
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px dashed #ced4da',
                          borderRadius: '6px',
                          padding: '0.35rem 0.6rem',
                          fontSize: '0.8125rem',
                          color: '#6c757d',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          gap: '0.25rem'
                        }}
                        onClick={() => handleAddTag(tag)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#0d6efd';
                          e.currentTarget.style.color = '#0d6efd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                          e.currentTarget.style.borderColor = '#ced4da';
                          e.currentTarget.style.color = '#6c757d';
                        }}
                      >
                        <i className="fas fa-plus" style={{ fontSize: '0.7rem' }}></i>
                        <span>{tag}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Form.Group>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={uploading || selectedFiles.length === 0}
        >
          {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadFileModal;
